import { useEffect, useState, useRef } from "react";
import { useAuth } from "../../auth/AuthContext";
import axios from "axios";
import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import { FaDownload, FaListUl, FaFileAlt } from "react-icons/fa";
import * as XLSX from "xlsx";

export default function ReportsScreen() {
  const { user, loading, userType } = useAuth();
  const [availableReports, setAvailableReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const loggedInUserId = user ? user.userID : null;
  const [running, setRunning] = useState(false);
  const [reportResult, setReportResult] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (user && userType && loggedInUserId) {
      const fetchReports = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/reports/available?userId=${loggedInUserId}`
          );
          setAvailableReports(response.data || []);
          if (response.data?.length > 0) {
            setSelectedReportId(response.data[0].reportId);
            // Initialize filterValues with keys from filters and their default values
            if (Array.isArray(response.data[0].filters)) {
              const initialFilters = {};
              response.data[0].filters.forEach((f) => {
                initialFilters[f.name] = f.default ?? "";
              });
              setFilterValues(initialFilters);
            }
          }
        } catch (error) {
          console.error("Failed to fetch reports:", error);
        }
      };
      if (!selectedReportId) {
        fetchReports();
      }
    }
  }, [user, userType, loggedInUserId, selectedReportId]);

  if (loading) return <div className="text-center mt-4">Loading...</div>;
  if (!user || !userType)
    return (
      <div className="text-center mt-4">Please log in to view reports.</div>
    );

  const selectedReport = availableReports.find(
    (r) => r.reportId === selectedReportId
  );

  const handleFilterChange = (name, value) => {
    setFilterValues((prev) => ({ ...prev, [name]: value }));
  };

  const downloadXLS = () => {
    if (
      !Array.isArray(reportResult) ||
      reportResult.length === 0 ||
      typeof reportResult[0] !== "object"
    ) {
      alert("No data to download.");
      return;
    }

    // Convert JSON to worksheet
    const worksheet = XLSX.utils.json_to_sheet(reportResult);

    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    // Find selected report name
    const selectedReport = availableReports.find(
      (r) => r.reportId === selectedReportId
    );
    const fileName =
      selectedReport && selectedReport.name
        ? `${selectedReport.name.replace(/\s+/g, "_")}_report.xlsx`
        : "report.xlsx";

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, fileName);
  };

  const runReport = async (mode) => {
    if (!selectedReportId) {
      alert("Please select a report first.");
      return;
    }

    setRunning(true);
    setNoResults(false);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/run`,
        {
          reportId: selectedReportId,
          filterValues: filterValues,
          userId: loggedInUserId,
          mode,
          userType: userType,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      // Handle custom API error responses
      if (res.data?.error) {
        alert(`Error: ${res.data.error}`);
        setReportResult([]);
        setNoResults(false);
        return;
      }

      // Handle success
      if (mode === "list" && res.data?.results) {
        setReportResult(res.data.results);
        setNoResults(res.data.results.length === 0);
      } else if (mode === "download" && res.data?.fileUrl) {
        window.open(res.data.fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error running report:", error);
        setReportResult([]);
        setNoResults(false);
      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400:
            alert(`Missing Data: ${data.error || "Missing or invalid data."}`);
            break;
          case 401:
            alert("Unauthorized: Please log in again.");
            break;
          case 403:
            alert("Forbidden: You do not have permission to run this report.");
            break;
          case 404:
            alert("Report not found. Please check the report selection.");
            break;
          case 500:
            alert("Server error: Please try again later.");
            break;
          default:
            alert(
              `Unexpected error (Code ${status}): ${
                data.error || "Unknown error"
              }`
            );
        }
      } else if (error.request) {
        alert(
          "Network error: Unable to reach the server. Check your connection."
        );
      } else {
        alert(`Unexpected error: ${error.message}`);
      }
    } finally {
      setRunning(false);
    }
  };

  // Remove extra white space by reducing margins and paddings
  return (
    <div className="min-vh-100 d-flex flex-column bg-light">
      {/* Top Menu */}
      <div className="container-fluid p-0">
        <MenuBar />
      </div>

      {/* Banner */}
      <div
        className="banner-image_sm d-flex align-items-center justify-content-center"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          height: "180px",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <h1 className="text-white fw-bold shadow-lg">Reports</h1>
      </div>

      {/* Main Content */}
      <div className="container-fluid mt-1 mb-1 flex-grow-1">
        <div className="row justify-content-center">
          <div className="col-lg-11 col-md-11 col-sm-11">
            <div className="card shadow-sm">
              <div className="card-body">
                {/* Report Selection */}
                <h5 className="card-title mb-1 d-flex align-items-center">
                  <FaFileAlt className="me-2 text-primary" /> Report:{" "}
                  <select
                    className="form-select mb-1"
                    value={selectedReportId}
                    onChange={(e) => {
                      setSelectedReportId(e.target.value);
                      const selected = availableReports.find(
                        (r) => r.reportId === e.target.value
                      );
                      if (Array.isArray(selected.filters)) {
                        const initialFilters = {};
                        selected.filters.forEach((f) => {
                          initialFilters[f.name] = f.default ?? "";
                        });
                        setFilterValues(initialFilters);
                      }
                      setReportResult([]);
                      setNoResults(false);
                    }}
                    disabled={availableReports.length === 0}
                  >
                    <option value="">-- Select --</option>
                    {availableReports.map((r) => (
                      <option key={r.reportId} value={r.reportId}>
                        {r.reportId} - {r.name}
                      </option>
                    ))}
                  </select>
                </h5>
                {availableReports.length === 0 && (
                  <div className="text-muted">
                    <em>No reports available.</em>
                  </div>
                )}

                {/* Filters */}
                {selectedReport && (
                  <div className="card p-3 mb-1 bg-light">
                    <div className="row">
                      {selectedReport.filters?.length === 0 && (
                        <div className="col-12 text-muted mb-1">
                          <em>
                            No filters for this report. Proceed to view results.
                          </em>
                        </div>
                      )}
                      {selectedReport.filters?.map((f) => (
                        <div className="col-md-4 mb-1" key={f.name}>
                          <label
                            className="form-label"
                            title={f.description || ""}
                          >
                            {f.label}
                            {f.description && (
                              <span
                                className="ms-1 text-info"
                                style={{ cursor: "help" }}
                                title={f.description}
                              >
                                ?
                              </span>
                            )}
                          </label>
                          {f.type === "select" && Array.isArray(f.options) ? (
                            <select
                              className="form-select"
                              value={filterValues[f.name] ?? f.default ?? ""}
                              onChange={(e) =>
                                handleFilterChange(f.name, e.target.value)
                              }
                            >
                              <option value="">
                                {f.placeholder || "-- Select --"}
                              </option>
                              {f.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : f.type === "multiselect" &&
                            Array.isArray(f.options) ? (
                            <select
                              className="form-select"
                              multiple
                              size={Math.min(f.options.length, 4)}
                              value={filterValues[f.name] ?? f.default ?? []}
                              onChange={(e) =>
                                handleFilterChange(
                                  f.name,
                                  Array.from(
                                    e.target.selectedOptions,
                                    (option) => option.value
                                  )
                                )
                              }
                            >
                              {f.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : f.type === "check" ? (
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`filter-${f.name}`}
                                checked={Boolean(
                                  filterValues[f.name] ?? f.default
                                )}
                                onChange={(e) =>
                                  handleFilterChange(f.name, e.target.checked)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`filter-${f.name}`}
                              ></label>
                            </div>
                          ) : (
                            <input
                              type={f.type === "date" ? "date" : "text"}
                              className="form-control"
                              placeholder={f.placeholder || ""}
                              value={filterValues[f.name] ?? f.default ?? ""}
                              onChange={(e) =>
                                handleFilterChange(f.name, e.target.value)
                              }
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Results & Buttons */}
                <div ref={resultsRef} className="mt-3">
                  {/* Buttons inside resultsRef */}
                  {selectedReport && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        onClick={() => runReport("list")}
                        disabled={running}
                      >
                        {running ? (
                          <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : (
                          <FaListUl className="me-1" />
                        )}
                        {running ? "Running..." : "Show Results"}
                      </button>
                      <button
                        className="btn btn-success d-flex align-items-center"
                        onClick={downloadXLS}
                        disabled={running || !reportResult.length}
                        title={!reportResult.length ? "Run a report first" : ""}
                      >
                        <FaDownload className="me-1" />
                        Download Excel
                      </button>
                    </div>
                  )}

                  {/* Results Table */}
                  {noResults && (
                    <div className="alert alert-info text-center">
                      No results found for the selected filters.
                    </div>
                  )}
                  {reportResult.length > 0 && (
                    <div className="table-responsive animate__animated animate__fadeIn">
                      <table className="table table-bordered table-hover table-sm">
                        <thead className="table-dark sticky-top">
                          <tr>
                            {Object.keys(reportResult[0]).map((key) => (
                              <th key={key}>{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reportResult.map((row, index) => (
                            <tr key={index}>
                              {Object.values(row).map((value, i) => (
                                <td key={i}>{value}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FooterPanel />
    </div>
  );
}
